import { createCollectionStore } from "../../../shared/data/createCollectionStore.js";
import {
  getPurchases,
  createPurchaseWithStockInward,
  deletePurchaseWithStockReversal
} from "../services/purchaseService.js";
import { recordVendorPaymentTally } from "../../invoices/services/paymentService.js";
import { calculateFifoAllocations } from "../../invoices/utils/paymentCalculations.js";

// One shared, cached copy of all purchase bills for every screen (loaded once per session)
export const purchaseStore = createCollectionStore(getPurchases, {
  errorMessage: "Unable to load purchases. Please check database connection."
});

export function usePurchases() {
  const { data: purchases, loading, error } = purchaseStore.useStore();
  const fetchPurchases = purchaseStore.refresh;

  const addPurchase = async (payload) => {
    const id = await createPurchaseWithStockInward(payload);
    await fetchPurchases();
    return id;
  };

  // Single-bill vendor payment, saved as a proper voucher so it can be edited or voided later
  const recordPayment = async (purchaseId, { paidAmount, paymentMode = "Cash", paymentDate, reference = "" } = {}) => {
    const purchase = purchases.find((p) => p.id === purchaseId);
    if (!purchase) throw new Error("Purchase bill not found.");
    const amount = Number(paidAmount || 0);
    const { allocations, totalApplied, unallocatedAmount } = calculateFifoAllocations([purchase], amount);

    const res = await recordVendorPaymentTally({
      vendorId: purchase.vendorId,
      vendorName: purchase.vendorName,
      vendorPhone: purchase.vendorPhone || "",
      companyId: purchase.companyId || null,
      amount,
      totalApplied,
      unallocatedAmount,
      paymentDate: paymentDate || new Date().toISOString().split("T")[0],
      paymentMode,
      reference,
      allocations: allocations.map((a) => ({
        ...a,
        purchaseId: purchase.id,
        billNo: purchase.vendorBillNo || "",
        billDate: purchase.billDate || ""
      }))
    });
    await fetchPurchases();
    return res;
  };

  const removePurchase = async (purchaseId) => {
    const res = await deletePurchaseWithStockReversal(purchaseId);
    await fetchPurchases();
    return res;
  };

  return {
    purchases,
    loading,
    error,
    refetch: fetchPurchases,
    addPurchase,
    recordPayment,
    removePurchase
  };
}

export default usePurchases;
